import { isNotNil } from 'rambda';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import {
  ScriptType,
  transformSpecialScripts,
} from '@/components/conversations/conversation-list/message-list-item/message-content/transform-scripts';
import SessionListItemActions from '@/components/sessions/session-list/session-list-item-actions';
import { SessionListItemAlert } from '@/components/sessions/session-list/session-list-item-alert';
import { useFriend } from '@/hooks/contacts/use-friends';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { type DraftType, useConversationSessionStore } from '@/stores/conversation-session';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { isDeletedById } from '@/utils/check-messages-status';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import { stripHtmlTags } from '@/utils/strip-html-tags';
import { transformMessageTextByType } from '@/utils/transform-message-text-by-type';
import SessionListItemTime from './session-list-item-time';
import SessionListItemTitle from './session-list-item-title';

interface Props {
  session: ConversationSessionsTypeUnion;
  onLinkClick?: () => void;
}

const QuoteDraft = ({ draft }: { draft: DraftType }) => {
  const { t } = useTranslation();
  const { Quote, Content } = draft;

  return (
    <span className='italic'>
      {`(${t('conversation.quote')} ${Quote?.UserDisplayName || Quote?.UserNickName || Quote?.UserID})`}
      {Content && ` :${Content}`}
    </span>
  );
};

export function SessionListItem({ session, onLinkClick }: Props) {
  const { ID, UserID, IsTop, SessionType, LastMsgID, LastMsgType, LastMsg } = session;
  const isGroup = SessionType === 'group';
  const key = generateSessionKey(SessionType, ID);
  const { t } = useTranslation();
  const { friend } = useFriend(UserID);
  const draft = useConversationSessionStore((s) => s.drafts[key]);
  const isNewSession = LastMsgID === 0;
  const isLastMessageDeleted = usePeriodicMessageStore((s) => isDeletedById(key, LastMsgID, s));

  const msgName =
    LastMsgType === ConversationMessageContentType.JoinGroup
      ? LastMsg?.match(/[：:]/)
        ? LastMsg?.split(/[：:]/)[0]
        : LastMsg?.substring(0, LastMsg.lastIndexOf(' '))
      : '';
  const displayName = friend?.RemarkName || msgName;

  return (
    <SessionListItemActions session={session}>
      <NavLink
        className={({ isActive }) =>
          cn(
            'border-b last-of-type:border-b-0 rounded-box group-hover:*:bg-base-100 peer-[.visible]:[&>div]:bg-base-200',
            { '*:bg-base-100': isActive },
          )
        }
        onClick={onLinkClick}
        to={(isGroup ? ROUTES.MESSAGES.GROUPS : ROUTES.MESSAGES.SESSIONS).$buildPath({ params: { id: ID } })}>
        <div className='flex w-full items-stretch gap-4 px-5 py-2 rounded-s transition-colors'>
          <div className='flex items-center'>
            <AvatarWithStatus
              isOnline={!IsTop && !isGroup && friend?.IsOnline}
              className='size-12'
              imageUrl={isGroup ? session.GroupImg : session.HeadImg}
              label={isGroup ? session.GroupName : session.NickName}
              isPin={IsTop}
              size={[48, 48]}
              defaultAvatar={isGroup ? 'group' : 'user'}
            />
          </div>

          <div className='flex flex-auto flex-col items-stretch gap-1'>
            <SessionListItemTitle session={session} />

            <div className='flex w-full flex-1 gap-1 text-body1'>
              <p className='line-clamp-1 w-24 flex-1 break-all text-metal-500'>
                <SessionListItemAlert
                  hasDraft={isNotNil(draft)}
                  session={session}
                />

                {draft ? (
                  draft.Quote ? (
                    <QuoteDraft draft={draft} />
                  ) : (
                    stripHtmlTags(draft?.Content || '')
                  )
                ) : isNewSession ? (
                  <span className='italic'>
                    {isGroup ? t('conversation.group.welcome') : t('conversation.message.new-session')}
                  </span>
                ) : isLastMessageDeleted ? (
                  <span className='italic'>{t('conversation.message.message-deleted')}</span>
                ) : LastMsgType === ConversationMessageContentType.Text ? (
                  transformSpecialScripts(session.LastMsgID, session.LastMsg, [], [ScriptType.Emoticon])
                ) : LastMsgType !== null ? (
                  transformMessageTextByType(
                    {
                      MsgType: LastMsgType,
                      Message: LastMsg,
                    },
                    displayName,
                  )
                ) : null}
              </p>
            </div>
          </div>

          <SessionListItemTime session={session} />
        </div>
      </NavLink>
    </SessionListItemActions>
  );
}
